import React, { useEffect, useState } from 'react';
import { doc, collection, addDoc, getDocs, query, where, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../Firebase'; 
import Empty from '../Img/empty.png';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Modal, 
  Divider,
  Alert
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { normalizeToHMinS, nowAsHMinS } from "../utils/timeFormat";

function Clocking() {

  const { t } = useTranslation();
  const [locationLoading, setLocationLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showClockInReminder, setShowClockInReminder] = useState(false);
  const [showClockOutReminder, setShowClockOutReminder] = useState(false);
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('Fetching location...');
  const [note, setNote] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [lastAction, setLastAction] = useState('');
  const [pendingEntry, setPendingEntry] = useState(null);
  const [history, setHistory] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);




  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);


  // Fetch User's Status
  useEffect(() => {
    const fetchClockedStatus = async () => {
      const user = auth.currentUser;
      if (!user) return;
  
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setIsClockedIn(userData.isClockedIn === true);
      }
    };
  
    fetchClockedStatus();
  }, []);

  
  // Fetch Clock in or Clock out reminders:
  useEffect(() => {
    const fetchReminderInfo = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();

      if (!userData?.assignedSiteId) return;

      const siteRef = doc(db, 'ConstructionSites', userData.assignedSiteId);
      const siteSnap = await getDoc(siteRef);
      if (!siteSnap.exists()) return;

      const siteData = siteSnap.data();
      const now = new Date();

      const [startHour, startMin] = siteData.startTime.split(':').map(Number);
      const [endHour, endMin] = siteData.endTime.split(':').map(Number);
      const startTime = new Date();
      startTime.setHours(startHour, startMin, 0, 0);
      const endTime = new Date();
      endTime.setHours(endHour, endMin, 0, 0);

      // Show clock-in reminder 10min before start
      if (!isClockedIn && now >= new Date(startTime.getTime() - 10 * 60000) && now < startTime) {
        setShowClockInReminder(true);
      } else {
        setShowClockInReminder(false);
      }

      // Show clock-out reminder after end time
      if (isClockedIn && now >= endTime) {
        setShowClockOutReminder(true);
      } else {
        setShowClockOutReminder(false);
      }
    };

    fetchReminderInfo();

    const interval = setInterval(fetchReminderInfo, 60 * 1000); // refresh every 60s
    return () => clearInterval(interval);
  }, [isClockedIn]);


  // Get user location (address display can be added later using reverse geocoding)
  useEffect(() => {
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const coords = { lat: latitude, lng: longitude };
        setLocation(coords);
        setAddress('Fetching address...'); // Update UI immediately

        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyCuhI0kt8VzP7uNw_HHzHLsCaIq5L5Xv2U`
          );
          const data = await response.json();
          if (data.results?.length > 0) {
            setAddress(data.results[0].formatted_address);
          } else {
            setAddress('Address not found');
          }
        } catch (error) {
          setAddress('Error fetching address');
        }
        setLocationLoading(false);
      },
      (err) => {
        setAddress('Location unavailable');
        console.error('Geolocation error:', err);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,     // Improve speed on mobile
        timeout: 10000,               // Fail fast after 10s
        maximumAge: 10000             // Reuse recent data if available
      }
    );
   
  }, []);
  

  // Refresh Map
  const handleRefreshLocation = () => {
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const coords = { lat: latitude, lng: longitude };
        setLocation(coords);
  
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyBVM5gMzAWI2_GiE91BZVTP1g6bOUyFwKk`
          );
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            setAddress(data.results[0].formatted_address);
          } else {
            setAddress('Address not found');
          }
        } catch (error) {
          setAddress('Error fetching address');
        }
        setLocationLoading(false);
      },
      () => {
        setAddress('Location unavailable');
        setLocationLoading(false);
      }
    );
  };
  

  // Clock in
  const handleClockIn = () => {
    const now = new Date();
    const entry = {
        type: 'Clock In',
        time: nowAsHMinS(),
        date: now.toLocaleDateString(),
        location: address,
        note: '',
        locationCoords: location  // will update later
    };
    setPendingEntry(entry);
    setLastAction('Clock In');
    setModalOpen(true);
  };


  // Clock Out
  const handleClockOut = () => {
    const now = new Date();
    const entry = {
        type: 'Clock Out',
        time: nowAsHMinS(),
        date: now.toLocaleDateString(),
        location: address,
        note: '', // will update later
        locationCoords: location 
        
    };
    setPendingEntry(entry);
    setLastAction('Clock Out');
    setModalOpen(true);
  };

  

  // Function to save the Clock In/Out in Database
  const saveToFirestore = async (entry) => {
    const user = auth.currentUser;
    if (!user) return;
  
    try {
      const userRef = doc(db, 'users', user.uid);
      const clockingCollection = collection(userRef, 'clocking history');
      await addDoc(clockingCollection, entry);
      console.log('Clocking entry saved.');
    } catch (error) {
      console.error('Error saving clocking entry:', error);
    }
  };

  
  // Confirm the Clock In/Out
  const handleConfirm = async () => {
    setIsSubmitting(true);

  const normalizedTime = normalizeToHMinS(pendingEntry?.time) || nowAsHMinS();

  const updatedEntry = {
    ...pendingEntry,
    time: normalizedTime,
    note: note || "—",
    timestamp: new Date().toISOString(),
  };
  
    // Save locally
    setHistory((prev) => [updatedEntry, ...prev]);
  
    // Save to Firestore
    await saveToFirestore(updatedEntry);


    // Update isClockedIn in user document if this is a Clock In
    const user = auth.currentUser;
    if (pendingEntry.type === 'Clock In') {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { isClockedIn: true });
    } else if (pendingEntry.type === 'Clock Out') {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { isClockedIn: false });
    }
    
  
    // Update UI
    setIsClockedIn(pendingEntry.type === 'Clock In');
    setModalOpen(false);
    setNote('');
    setPendingEntry(null);
    setIsSubmitting(false);
  };  
  

  // Fetch all clocking history
  const fetchClockingHistory = async (showAll = false) => {
    const user = auth.currentUser;
    if (!user) return;

    const today = new Date().toLocaleDateString();
    const userRef = doc(db, 'users', user.uid);
    const clockingCollection = collection(userRef, 'clocking history');

    const q = showAll
      ? query(clockingCollection)
      : query(clockingCollection, where('date', '==', today));

    const querySnapshot = await getDocs(q);
    const entries = querySnapshot.docs.map(doc => doc.data());

    const sorted = entries.sort((a, b) => {
      const getDateTime = (entry) => {
        const [day, month, year] = entry.date.split('/');
        const [time, meridian] = entry.time.split(' ');
        let [hour, minute] = time.split(':').map(Number);

        if (meridian === 'PM' && hour !== 12) hour += 12;
        if (meridian === 'AM' && hour === 12) hour = 0;

        return new Date(year, month - 1, day, hour, minute);
      };

      return getDateTime(b) - getDateTime(a); 
    });

    setHistory(sorted);
  };

  useEffect(() => {
    fetchClockingHistory(); 
  }, []);
  

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, p: 2 }}>
      {/* Time Display */}
      <Typography variant="h2" fontWeight="bold" textAlign="center" mb={0}>
        {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Typography>
      <Typography variant="subtitle1" textAlign="center" mb={3}>
        {currentTime.toLocaleDateString()}
      </Typography>



      {showClockInReminder && (
        <Alert severity="warning" sx={{marginBottom: '10px', fontWeight:"bold"}}>
          ⏰ {t("Vous commencez dans 10 minutes. N'oubliez pas de pointer !")}
        </Alert>
      )}

      {showClockOutReminder && (
        <Alert severity="error" sx={{marginBottom: '10px', fontWeight:"bold"}}>
          ⏰ {t("La journée est terminée. N'oubliez pas de pointer la sortie !")}
        </Alert>
      )}


      {/* Clock In / Clock Out Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      {!isClockedIn ? (
        <Button
            variant="contained"
            onClick={handleClockIn}
            disabled={locationLoading}
            sx={{
            backgroundColor: '#c93d3d',
            borderRadius: 3,
            py: 1.5,
            fontWeight: 'bold',
            fontSize: '1rem',
            '&:hover': {
                backgroundColor: '#a93131',
            },
            }}
        >
            <PlayArrowIcon sx={{marginRight: '5px'}}/> {t("Pointer l’entrée")}
        </Button>
        ) : (
        <Button
            variant="outlined"
            onClick={handleClockOut}
            disabled={locationLoading}
            sx={{
            borderRadius: 3,
            py: 1.5,
            fontWeight: 'bold',
            fontSize: '1rem',
            }}
        >
            <StopIcon sx={{marginRight: '5px'}}/> {t("Pointer la sortie")}
        </Button>
        )}
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: '#fff', borderRadius: 3, boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.1)'}}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center">
            <ForkRightIcon sx={{ mr: 1 }} /> {t("Votre position actuelle")}
          </Typography>
          <RefreshIcon
            onClick={handleRefreshLocation}
            sx={{
              cursor: 'pointer',
              color: '#c93d3d',
              '&:hover': { color: '#a93131' },
            }}
          />
        </Box>
        {/* Map Placeholder */}
        {location && location.lat && location.lng && (
          <Box
              sx={{
              height: 120,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2,
              }}
          >
              <img
              src={`https://maps.googleapis.com/maps/api/staticmap?center=${location.lat},${location.lng}&zoom=16&size=600x200&maptype=roadmap&markers=color:red%7C${location.lat},${location.lng}&key=AIzaSyCuhI0kt8VzP7uNw_HHzHLsCaIq5L5Xv2U`}
              alt="Map location"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
          </Box>
        )}



        {/* Location Display */}
        {locationLoading ? (
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('Chargement de la position...')}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {address}
          </Typography>
        )}


        {/* Status */}
        <Typography variant="body2" sx={{ mb: 2 }}>
        <b>{t("Statut")}:</b> {isClockedIn ? t("Vous êtes pointé") : t("Vous n’êtes pas pointé")}
        </Typography>
      </Paper>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box
              sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              bgcolor: 'background.paper',
              p: 3,
              borderRadius: 2,
              boxShadow: 24,
              }}
          >
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("Confirmer")} {lastAction}
              </Typography>
              {pendingEntry && (
              <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{t("Date et heure")}:</b> {pendingEntry.time} - {pendingEntry.date}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{t("Emplacement")}:</b> {pendingEntry.location}
                  </Typography>
                  <TextField
                  label={t("Note facultative")}
                  fullWidth
                  multiline
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{ my: 2 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleConfirm}
                    fullWidth
                    disabled={isSubmitting}
                    sx={{
                      backgroundColor: '#3dc97c',
                      fontWeight: 'bold',
                      borderRadius: 2,
                    }}
                  >
                    {isSubmitting ? t("En cours...") : t("Confirmer")}
                  </Button>
              </>
              )}
          </Box>
      </Modal>


    <Paper elevation={1} sx={{ p: 2, mt: 3, borderRadius: 3, boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.1)' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          📅 {t("Historique des pointages du jour")}
        </Typography>
        {history.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ my: 4 }}>
            <Box
              component="img"
              src={Empty}
              alt="Empty History"
              sx={{ width: 120, height: 'auto', mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t("Aucune activité de pointage pour aujourd'hui.")}
            </Typography>
          </Box>
        ) : (
          history.map((entry, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Box display="flex" alignItems="center">
                    <CalendarMonthIcon sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {entry.time} - {entry.date} 
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Emplacement")}: {entry.location}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {t("Note")}: {entry.note}
                  </Typography>
                </Box>
                {entry.type === 'Clock In' ? (
                  <WorkHistoryIcon sx={{ color: 'green' }} />
                ) : (
                  <DirectionsWalkIcon sx={{ color: 'red' }} />
                )}
              </Box>
            </Box>
          ))
        )}

        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowAllHistory(true);
              fetchClockingHistory(true); 
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              borderRadius: 2,
              color: '#040303',
              borderColor: 'gray',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              }
            }}
          >
            {t("Voir tout l'historique de pointage")}
          </Button>
        </Box>
    </Paper>
    </Box>
  );
}

export default Clocking;
